import React, { useState, useEffect } from "react";

const SECTIONS = [
  { id: "introduction", num: "01", title: "Introduction" },
  { id: "information", num: "02", title: "Information We Collect" },
  { id: "usage", num: "03", title: "How We Use Information" },
  { id: "sharing", num: "04", title: "Sharing of Information" },
  { id: "children", num: "05", title: "Children's Privacy" },
  { id: "security", num: "06", title: "Data Security" },
  { id: "retention", num: "07", title: "Data Retention" },
  { id: "rights", num: "08", title: "Your Rights" },
  { id: "contact", num: "09", title: "Contact Us" },
];

export default function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState("introduction");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-primary-gray">
      {/* Page header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-sm text-mute-text mb-1">Fuelife Explorers Pvt Ltd</p>
          <h1 className="text-primary-black text-[28px] font-semibold">Privacy Policy</h1>
          <p className="text-sm text-mute-text mt-1">Last Updated: 18 June 2026</p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-5xl mx-auto px-8 py-8 flex gap-8 items-start">

        {/* Side nav */}
        <aside className="w-48 flex-shrink-0 sticky top-8">
          <p className="text-xs font-semibold text-mute-text uppercase tracking-widest mb-3">
            Contents
          </p>
          <nav className="flex flex-col">
            {SECTIONS.map(({ id, num, title }) => {
              const isActive = activeSection === id;
              return (
                <button
                  key={id}
                  onClick={() => scrollTo(id)}
                  className={`text-left text-sm py-2 px-3 rounded-lg transition-colors ${
                    isActive
                      ? "bg-white font-semibold text-primary-black"
                      : "text-mute-text hover:text-primary-black hover:bg-white"
                  }`}
                >
                  <span className="text-xs text-mute-text mr-1.5">{num}</span>
                  {title}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0 space-y-4">

          <Section id="introduction" title="Introduction">
            <p>
              Welcome to Travely. This Privacy Policy explains how{" "}
              <span className="font-semibold text-primary-black">Fuelife Explorers</span> collects,
              uses, and protects information when users access and use our application.
            </p>
            <p className="mt-2">
              Our application is designed to provide educational institutions, students, parents,
              and tour coordinators with access to tour itineraries, trip updates, and trip
              tracking services.
            </p>
          </Section>

          <Section id="information" title="Information We Collect">
            <p>Depending on how the app is used, we may collect:</p>
            <SubSection title="Account Information">
              <BulletList items={["Name", "Email address", "Phone number", "Institution or school name"]} />
            </SubSection>
            <SubSection title="Trip Information">
              <BulletList items={[
                "Tour registration details",
                "Itinerary information",
                "Travel participation records",
                "Tracking tour information",
              ]} />
            </SubSection>
            <SubSection title="Location Information">
              <p>
                During active tours, the app may collect and display location information to
                facilitate trip tracking, safety monitoring, and coordination.
              </p>
            </SubSection>
            <SubSection title="Device Information">
              <BulletList items={["Device type", "Operating system", "App version", "Diagnostic and usage data"]} />
            </SubSection>
          </Section>

          <Section id="usage" title="How We Use Information">
            <p>We use collected information to:</p>
            <BulletList items={[
              "Provide access to tour itineraries",
              "Coordinate and manage educational trips",
              "Enable trip tracking and safety monitoring",
              "Communicate tour updates and notifications",
              "Improve app performance and user experience",
              "Comply with legal obligations",
            ]} />
          </Section>

          <Section id="sharing" title="Sharing of Information">
            <p>We may share information with:</p>
            <BulletList items={[
              "Participating educational institutions",
              "Authorized tour coordinators",
              "Service providers supporting app operations",
            ]} />
            <p className="mt-3 font-semibold text-primary-black">
              We do not sell personal information.
            </p>
          </Section>

          <Section id="children" title="Children's Privacy">
            <p>
              If students under the age of 18 use the app, access should be provided under the
              authorization of their educational institution, parent, or legal guardian where
              required by applicable law.
            </p>
          </Section>

          <Section id="security" title="Data Security">
            <p>
              We implement reasonable technical and organizational measures to protect information
              against unauthorized access, disclosure, or misuse.
            </p>
          </Section>

          <Section id="retention" title="Data Retention">
            <p>
              We retain personal information only as long as necessary to provide services, comply
              with legal obligations, resolve disputes, and enforce agreements.
            </p>
          </Section>

          <Section id="rights" title="Your Rights">
            <p>
              Subject to applicable laws, users may request access, correction, or deletion of
              personal information by contacting us.
            </p>
          </Section>

          <Section id="contact" title="Contact Us">
            <div className="space-y-1.5 text-sm">
              <p className="font-semibold text-primary-black">Fuelife Explorers Pvt Ltd</p>
              <p>
                Email:{" "}
                <a
                  href="mailto:shafeeque@fuelifeexplorers.com"
                  className="text-blue-600 hover:underline"
                >
                  shafeeque@fuelifeexplorers.com
                </a>
              </p>
              <p>Address: AG Plaza, Sukh Sagar Road, Kammanahali, Bangalore, Karnataka</p>
            </div>
          </Section>

        </main>
      </div>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <div
      id={id}
      style={{ scrollMarginTop: "32px" }}
      className="bg-white rounded-xl border border-gray-200 p-6"
    >
      <h2 className="text-primary-black text-lg font-semibold mb-3 pb-3 border-b border-gray-100">
        {title}
      </h2>
      <div className="text-sm text-gray-600 leading-relaxed">{children}</div>
    </div>
  );
}

function SubSection({ title, children }) {
  return (
    <div className="mt-4">
      <p className="text-sm font-semibold text-primary-black mb-1.5">{title}</p>
      {children}
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-1 mt-1">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2">
          <span className="mt-2 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}
